import Backbone from 'backbone';

import {Sequence, Sequence as ComponentSequence} from 'components/Sequence';
import {Strand} from 'constants/MolbioConstants';
import * as selectables from 'molbio/maps/selectables/Selectables';
import {Optional} from 'util/Types';

export interface FeatureSearchResultFeature {
  name(): string;
  // The index of the name string where the query begins to match.
  start(): number;
  // The index of the name string where the query
  end(): number;
  strand(): Strand;
  type(): string;
  color(): string;
  click(sequence: Sequence, shiftKey: Optional<boolean>): void;
}

export interface FeatureSearchResultAttributes {
  feature: FeatureSearchResultFeature;
  start: number;
  end: number;
}

class FeatureSearchResult extends Backbone.Model {
  feature(): FeatureSearchResultAttributes['feature'] {
    return this.get('feature');
  }

  start(): FeatureSearchResultAttributes['start'] {
    return this.get('start');
  }

  end(): FeatureSearchResultAttributes['end'] {
    return this.get('end');
  }
}

class FeatureSearchResults extends Backbone.Collection<FeatureSearchResult> {
  static initClass(): void {
    this.prototype.model = FeatureSearchResult;
  }

  // TODO (molbio): Replace with simpler method that takes a single model and generates a key.
  comparator = (r1: FeatureSearchResult, r2: FeatureSearchResult): number => {
    const [n1, n2] = Array.from([r1.feature().name().toLowerCase(), r2.feature().name().toLowerCase()]);
    if (n1 < n2) {
      return -1;
    } else if (n1 === n2) {
      return 0;
    } else {
      return 1;
    }
  };
}

FeatureSearchResults.initClass();

interface DNASearchResultAttributes {
  start: number;
  end: number;
  strand: Strand;
  hover: boolean;
  selected: boolean;
  active: boolean;
  sequence: ComponentSequence;
}

class DNASearchResult extends selectables.RegionSelectable<ComponentSequence> {
  defaults(): Partial<DNASearchResultAttributes> {
    return {...super.defaults(), hover: false};
  }

  start(): DNASearchResultAttributes['start'] {
    return this.get('start');
  }

  end(): DNASearchResultAttributes['end'] {
    return this.get('end');
  }

  layerStart(): number {
    return this.start();
  }

  layerEnd(): number {
    return this.end();
  }

  color(): string {
    return 'red';
  }

  uid(): string {
    return this.cid;
  }

  type(): string {
    return 'search-result';
  }

  hover(): DNASearchResultAttributes['hover'] {
    return this.get('hover');
  }

  strand(): DNASearchResultAttributes['strand'] {
    return this.get('strand');
  }

  sequence(): DNASearchResultAttributes['sequence'] {
    return this.get('sequence');
  }

  click(sequence: ComponentSequence, shiftKey: boolean = false): void {
    sequence.get('selection').select(this);
  }

  dblClick(sequence: ComponentSequence, shiftKey: boolean = false): void {
    this.click(sequence, shiftKey);
  }
}

class DNASearchResults extends selectables.Collection<DNASearchResult> {
  static initClass(): void {
    this.prototype.model = DNASearchResult;
  }

  // TODO (molbio): Rename to distinguish it from the default Backbone.slice() and simplify
  // @ts-expect-error: This function type incorrectly extends the base backbone type.
  slice(
    start: number,
    end: number,
    seq: ComponentSequence,
    onlyShowSelected: boolean = false
  ): Array<DNASearchResult> {
    const selections = onlyShowSelected ? this.selected() : this.models;

    return selections.filter((dnaSearchResult) => {
      const _seq = seq.get('_sequence');
      const searchseq = dnaSearchResult.get('sequence').get('_sequence');
      const dStart = dnaSearchResult.start();
      const dEnd = dnaSearchResult.end();
      const onSlice =
        dStart === dEnd ||
        (dStart > dEnd && (end > dStart || start < dEnd)) ||
        (dStart < end && dEnd > start);
      return _seq.cid === searchseq.cid && onSlice;
    });
  }
}

DNASearchResults.initClass();

export {DNASearchResult, DNASearchResults, FeatureSearchResult, FeatureSearchResults};
